/**
 * A PDApp's Variables and secrets — its settings section (plan docs/plans/2026-10-21_PDAPP_ENV_VARS.md). The list is
 * the app's own `_appsystem/env.json` (content: it travels with the app); every variable becomes an environment
 * variable of the app's backend code. A SECRET names a keybook key (picked, or created here) whose value stays
 * encrypted in the keybook — this screen never shows it — and the app reads it only through a grant a person made: a
 * re-uploaded app's secrets show "needs a key" until someone connects them (one click when they can read that key).
 *
 * Editing the app needs Read-only on every key it holds (the server's app-reach gate). Like the app's access list:
 * a viewer sees a key's name only when they can read the key; one who may only manage the app folder (not edit
 * the app) may only take a key away from the app — never change its variables.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { KeyRound, Plus, Pencil, Trash2, AlertTriangle, Lock, Loader2, Search, Copy, Check, Eye, EyeOff, Info, Variable, Type as TypeIcon, ClipboardPaste, BookKey, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { getActiveTenantId, errorText } from '@papan/api-client'
import { useRealtimeOptional } from '@papan/realtime/client'
import { useConfirmOptional } from '@papan/core-ui/ui'
import { SANDBOX_ENV_NAME_RE, appEnvNameError } from '@papan/sandbox/env-names'
import { keybookApi, type AppEnvVar } from '../client/api.js'
import { KeyChip, SecretBadge } from './KeyChip.js'
import { Choice } from './Choice.js'
import { KeyFieldPicker } from './KeyFieldPicker.js'

/** The server's rule, spelled once (`@papan/sandbox/env-names` `appEnvNameError`) — the server decides; this only saves
 *  a round trip and picks the message. */
export function appEnvNameProblem(name: string): 'format' | 'reserved' | null {
  if (!appEnvNameError(name)) return null
  return SANDBOX_ENV_NAME_RE.test(name) ? 'reserved' : 'format'
}

type Draft = {
  /** The variable being edited (null = a new one). */
  original: string | null
  name: string
  kind: 'plain' | 'secret'
  value: string
  source: 'new' | 'keybook'
  entryId: string | null
  field: string
  /** A secret's note (what it is for) — kept in env.json, travels with the app. */
  description: string
}

const emptyDraft = (): Draft => ({ original: null, name: '', kind: 'plain', value: '', source: 'new', entryId: null, field: '', description: '' })

/** The list gets a search box from this many variables on — below it, a filter only adds noise. */
const SEARCH_FROM = 6

/** Rows a page of the list shows; more than this and a pager appears under it. */
const PAGE_SIZE = 10

/** A key's field the row names only when it isn't this one (a secret made here always uses it). */
const DEFAULT_FIELD = 'key'

/** The list is ONE grid (the rows are its subgrids), so every row's columns line up whatever buttons it carries. It
 *  follows the list's own width (a container query), not the viewport's: the settings can sit in a narrow pane on a
 *  wide screen. Below 36rem each row stacks. The value column gets the larger share. */
const LIST_GRID = '@xl/envlist:grid @xl/envlist:grid-cols-[minmax(0,2fr)_auto_minmax(0,3fr)_auto] @xl/envlist:items-center @xl/envlist:gap-x-3'
const ROW_GRID = '@xl/envlist:col-span-full @xl/envlist:grid @xl/envlist:grid-cols-subgrid @xl/envlist:gap-x-3'
const FULL_ROW = '@xl/envlist:col-span-full'

/** The small icon buttons beside a name or value (copy): hidden until the row is hovered, only where there IS hover (a
 *  mouse); on touch they always show. */
const HOVER_ICON_BTN = 'shrink-0 p-1 rounded themed-text-muted hover:themed-text pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:focus-visible:opacity-100'
/** A row's actions (Edit, Remove): one size, always shown. */
const ACTION_BTN = 'p-1.5 rounded themed-text-muted hover:themed-bg-tertiary disabled:opacity-40'

/** One look for every notice above the list (not installed, keybook off, env.json problems, blocked keys). */
function Notice({ icon, testId, children }: { icon: ReactNode; testId: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 px-3 py-2 mb-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs themed-text" data-testid={testId}>
      <span className="text-amber-400 shrink-0 mt-0.5">{icon}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}


/** `installed` — false: the app runs as the person (not installed, or its own folder set to No access), so its runs
 *  get none of these. */
export function AppEnvSettings({ folderId, appName, installed = true, reloadSignal, editRequest }: { folderId: string; appName: string; installed?: boolean
  /** Changes when the host saw the app change (its env.json written elsewhere): reload the list. An open edit is kept. */
  reloadSignal?: number
  /** Display only: open this variable's editor (a new `n` = a new request). */
  editRequest?: { name: string; n: number } }) {
  const { t } = useTranslation('keybook')
  const confirm = useConfirmOptional()
  const [vars, setVars] = useState<AppEnvVar[] | null>(null)
  const [canEdit, setCanEdit] = useState(false)
  const [keybookEnabled, setKeybookEnabled] = useState(true)
  const [maxVars, setMaxVars] = useState(64)
  const [hidden, setHidden] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [unusedKeys, setUnusedKeys] = useState<Array<{ entryId: string; label: string | null }>>([])
  // Display only: the list's filter, the eye on a new secret being typed, and what was just copied (a fresh object per
  // copy, so copying the same thing again restarts the check mark and is announced again).
  const [query, setQuery] = useState('')
  const [reveal, setReveal] = useState(false)
  const [copied, setCopied] = useState<{ name: string; what: 'name' | 'value'; n: number } | null>(null)
  // Display only: which page of the list is showing (PAGE_SIZE rows a page).
  const [page, setPage] = useState(0)
  /** What the render knows about paging, for the focus effect above it: the page shown, and the page a name is on. */
  const pageInfoRef = useRef<{ page: number; locate: (name: string) => number | null } | null>(null)
  // A key renamed, retyped, deleted or re-shared in the Keybook shows here at once: when the Keybook says a key of this
  // organization changed, the list is reloaded with the section's own `load` (a failed reload keeps what is on screen,
  // an open form included, and the latest load wins). Not while a save / remove / connect runs — that action re-lists
  // itself; the event waits for it. The key picker refreshes its own key list (KeyFieldPicker). `load` is reached
  // through a ref because it is defined further down.
  const loadRef = useRef<(() => Promise<void>) | null>(null)
  const busyRef = useRef(false)
  useEffect(() => { busyRef.current = busy }, [busy])
  const onKeybookEvent = useRealtimeOptional()?.onKeybookEvent
  useEffect(() => {
    if (!onKeybookEvent) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const reload = () => {
      timer = null
      if (busyRef.current) { timer = setTimeout(reload, 300); return }
      void loadRef.current?.()
    }
    const off = onKeybookEvent(event => {
      const tenantId = getActiveTenantId()
      if (tenantId && event.tenantId !== tenantId) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(reload, 300)
    })
    return () => { off(); if (timer) clearTimeout(timer) }
  }, [onKeybookEvent])
  // A typed secret goes back behind dots whenever the form closes or switches what it is entering.
  const formShape = draft ? `${draft.original ?? ''}|${draft.kind}|${draft.source}` : null
  useEffect(() => { setReveal(false) }, [formShape])
  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(null), 1500)
    return () => clearTimeout(id)
  }, [copied])
  // The form replaces its row, so closing it would drop focus to the page: give it back to the row's Edit (Cancel, or
  // the saved name once the list has it), or to Add after a new one is cancelled.
  const sectionRef = useRef<HTMLElement>(null)
  const focusAfterForm = useRef<string | null>(null)
  useEffect(() => {
    const target = focusAfterForm.current
    const root = sectionRef.current
    if (draft) return
    // While a save is still re-listing, the row's buttons are disabled and can't take focus: wait, without a look.
    if (target === null || !root || busy) return
    // A saved row the list shows on another page: turn to it first (no look spent), then focus it.
    const info = pageInfoRef.current
    const onPage = target === '' || !info ? null : info.locate(target)
    if (info && onPage !== null && onPage !== info.page) { setPage(onPage); return }
    const el = target === ''
      ? root.querySelector<HTMLElement>('[data-testid="pdapp-env-add"]')
      : [...root.querySelectorAll<HTMLElement>('[data-env-edit]')].find(e => e.dataset.envEdit === target)
    // ONE look, whether it lands or not: a Save's once its re-list is in, a Cancel's on the render that closed the form
    // (Cancel is off while busy, so its row's Edit or Add is there then). A row a search hides, or one that is gone,
    // is simply not focused — never later, when the list or the page changes.
    if (el) el.focus()
    focusAfterForm.current = null
  }, [draft, vars, busy, page])

  // Latest load wins: a reload started before a write must not land after the write's own and bring a row back.
  const loadSeq = useRef(0)
  const loadedOnce = useRef(false)
  const load = useCallback(async () => {
    const seq = ++loadSeq.current
    try {
      const r = await keybookApi.getAppEnv(folderId)
      if (seq !== loadSeq.current) return
      setVars(r.vars); setCanEdit(r.canEdit); setKeybookEnabled(r.keybookEnabled); setMaxVars(r.maxVars)
      setProblems(r.problems ?? []); setUnusedKeys(r.unusedKeys ?? [])
      setHidden(false)
      loadedOnce.current = true
    } catch {
      if (seq !== loadSeq.current) return
      // The FIRST load refused: not someone who may see the app's settings (or an older API) — the section isn't theirs.
      // A later REFRESH that fails (a blip, a restart) keeps what is on screen — an open edit included.
      if (!loadedOnce.current) setHidden(true)
    }
  }, [folderId])
  // One effect for the first load and every reload the host asks for (`reloadSignal`).
  useEffect(() => { void load() }, [load, reloadSignal])

  const startEdit = (v: AppEnvVar) => {
    setError(null)
    setDraft(v.kind === 'plain'
      ? { ...emptyDraft(), original: v.name, name: v.name, kind: 'plain', value: v.value ?? '' }
      : { ...emptyDraft(), original: v.name, name: v.name, kind: 'secret', source: 'keybook', entryId: v.secret?.entryId ?? null, field: v.secret?.field ?? '', description: v.secret?.description ?? '' })
  }

  // Display only: the host asks to open one variable's editor (the setup's key pick, "Choose a key" in Settings). The
  // section comes into view; the editor opens only for someone who can edit, and never over a form already open (its
  // unsaved edit is kept). Each request is answered once, when the list is in.
  // A request already there when the section mounts (left from another app's page) is not this one's to answer.
  const editHandled = useRef<number | null>(editRequest?.n ?? null)
  useEffect(() => {
    // While a save re-lists, wait for it (the list it brings back is the one to open from).
    if (!editRequest || editHandled.current === editRequest.n || vars === null || busy) return
    editHandled.current = editRequest.n
    sectionRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    if (!canEdit || draft) return
    const v = vars.find(x => x.name === editRequest.name)
    if (v) startEdit(v)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- answer each request once; startEdit only sets state
  }, [editRequest, vars, canEdit, draft, busy])

  if (hidden || vars === null) return null

  const nameProblem = draft ? appEnvNameProblem(draft.name) : null
  const duplicate = !!draft && draft.name !== draft.original && vars.some(v => v.name === draft.name)
  const canSave = !!draft && !busy && !nameProblem && !duplicate && (
    draft.kind === 'plain'
      ? true
      : draft.source === 'new' ? draft.value.length > 0 : !!draft.entryId && !!draft.field
  )

  /** After an error: did the write land anyway (the response was lost, or a retried rename already happened)? The
   *  variable is there as asked and, for a rename, the old name is gone. */
  const landed = async (name: string, matches: (v: AppEnvVar) => boolean, original: string | null): Promise<'yes' | 'no' | 'unknown'> => {
    try {
      const now = (await keybookApi.getAppEnv(folderId)).vars
      const v = now.find(x => x.name === name)
      return v && matches(v) && (!original || original === name || !now.some(x => x.name === original)) ? 'yes' : 'no'
    } catch {
      return 'unknown'
    }
  }

  const save = async () => {
    if (!draft || !canSave) return
    setBusy(true); setError(null)
    try {
      // A rename is one write on the server: the new name saved, the old one gone — never both, never neither.
      const renameFrom = draft.original && draft.original !== draft.name ? { renameFrom: draft.original } : {}
      if (draft.kind === 'plain') {
        await keybookApi.setAppEnv(folderId, draft.name, { kind: 'plain', value: draft.value, ...renameFrom })
          .catch(async e => { if (await landed(draft.name, v => v.kind === 'plain' && v.value === draft.value, draft.original) !== 'yes') throw e })
      } else if (draft.source === 'new') {
        const tenantId = getActiveTenantId()
        if (!tenantId) throw new Error(t('appEnv.noKeybook'))
        // A new key in the app's keybook, owned by this person — then given to the app. If giving it definitely failed
        // (the re-read shows the app doesn't hold it), the key made for it goes too. If that can't be told (the re-read
        // failed as well), it stays: a retry then makes a second key and the first is left unused — better than an app
        // bound to a deleted key. Residual: a save still running on the server when the client gave up reads as "not
        // saved" — its key is removed and the variable shows "unavailable"; re-adding it fixes it.
        const created = await keybookApi.createEntry(tenantId, {
          ownerType: 'email',
          sourceLabel: `${appName} · ${draft.name}`,
          credentialType: 'api_key',
          data: { credentialType: 'api_key', fields: { key: draft.value } },
        })
        try {
          await keybookApi.setAppEnv(folderId, draft.name, { kind: 'secret', entryId: created.entryId, field: 'key', description: draft.description, ...renameFrom })
        } catch (e) {
          // A lost response is not a failed save: the new key is removed only on a definite "not saved". Unknown (the
          // re-read failed too) keeps it — an unused key is better than an app bound to a deleted one.
          const outcome = await landed(draft.name, v => v.secret?.entryId === created.entryId, draft.original)
          if (outcome !== 'yes') {
            if (outcome === 'no') await keybookApi.deleteEntry(created.entryId).catch(() => {})
            throw e
          }
        }
      } else {
        await keybookApi.setAppEnv(folderId, draft.name, { kind: 'secret', entryId: draft.entryId!, field: draft.field, description: draft.description, ...renameFrom })
          .catch(async e => { if (await landed(draft.name, v => v.secret?.entryId === draft.entryId && v.secret?.field === draft.field, draft.original) !== 'yes') throw e })
      }
      setDraft(null)
      await load()
    } catch (e: any) {
      const text = errorText(e, t('appEnv.saveFailed'))   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    } finally {
      setBusy(false)
    }
  }

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null)
    try {
      await fn()
      await load()
    } catch (e: any) {
      const text = errorText(e, t('appEnv.saveFailed'))   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    } finally {
      setBusy(false)
    }
  }

  const ask = async (title: string, message: string, confirmText: string) => confirm
    ? confirm({ title, message, confirmText, type: 'danger' })
    : window.confirm(title)

  /** An editor removes the variable from env.json (its key goes with it if no other variable names it); a manager who
   *  may only lower / remove takes the secret's KEY away from the app (the variable stays, needing a key). */
  const remove = async (v: AppEnvVar) => {
    const takeAway = !canEdit && v.kind === 'secret'
    const ok = takeAway
      ? await ask(t('appEnv.takeKeyTitle', { name: v.name }), t('appEnv.takeKeyBody'), t('appEnv.takeKey'))
      : await ask(t('appEnv.removeTitle', { name: v.name }), v.kind === 'secret' ? t('appEnv.removeSecretBody') : t('appEnv.removePlainBody'), t('appEnv.remove'))
    if (ok) await run(() => keybookApi.deleteAppEnv(folderId, v.name))
  }

  /** One click: give the app the key env.json already names (a re-upload, or one taken away) — the viewer can read it. */
  const connect = (v: AppEnvVar) => run(() => keybookApi.setAppEnv(folderId, v.name, {
    kind: 'secret', entryId: v.secret!.entryId!, field: v.secret!.field!, description: v.secret!.description ?? undefined,
  }))

  const takeUnusedKey = async (k: { entryId: string; label: string | null }) => {
    const name = k.label ?? t('appEnv.aKey')
    if (await ask(t('appEnv.takeKeyTitle', { name }), t('appEnv.takeUnusedBody'), t('appEnv.takeKey'))) {
      await run(() => keybookApi.deleteAppEnvKey(folderId, k.entryId))
    }
  }

  const blockedKeys = vars.filter(v => v.secret?.status === 'no-access').length
  const atMax = vars.length >= maxVars

  // Display only: what the search box matches (name, a Text value, a secret's key label and note). The row being
  // edited always stays, so a filter never hides an open form. The filter applies only while its box is shown: drop
  // below six variables and it lets go, never stranding the list.
  const searchable = vars.length >= SEARCH_FROM
  const q = searchable ? query.trim().toLowerCase() : ''
  const shownVars = !q ? vars : vars.filter(v => v.name === draft?.original || [v.name, v.kind === 'plain' ? v.value : v.secret?.label, v.secret?.description]
    .some(s => !!s && s.toLowerCase().includes(q)))
  // Paging (display only) over what the search shows; a page past the end (the list shrank) shows the last one.
  const pageCount = Math.max(1, Math.ceil(shownVars.length / PAGE_SIZE))
  const curPage = Math.min(page, pageCount - 1)
  const pageVars = shownVars.slice(curPage * PAGE_SIZE, (curPage + 1) * PAGE_SIZE)
  pageInfoRef.current = {
    page: curPage,
    locate: name => { const i = shownVars.findIndex(v => v.name === name); return i < 0 ? null : Math.floor(i / PAGE_SIZE) },
  }
  // The stored page follows what is shown: a list that shrank keeps the page it falls back to, so a later re-list
  // that grows it again doesn't jump to a page nobody turned to.
  if (page !== curPage) setPage(curPage)
  loadRef.current = load

  const copy = (name: string, what: 'name' | 'value', text: string) => {
    navigator.clipboard?.writeText(text).then(() => setCopied(c => ({ name, what, n: (c?.n ?? 0) + 1 })), () => {})
  }
  const isCopied = (name: string, what: 'name' | 'value') => copied?.name === name && copied.what === what

  const copyButton = (v: AppEnvVar, what: 'name' | 'value', text: string) => {
    const label = what === 'name' ? t('appEnv.copyName', { name: v.name }) : t('appEnv.copyValue', { name: v.name })
    const done = isCopied(v.name, what)
    return (
      <button
        type="button"
        onClick={() => copy(v.name, what, text)}
        className={HOVER_ICON_BTN}
        aria-label={label}
        title={done ? t('appEnv.copied') : label}
        data-testid={what === 'name' ? 'pdapp-env-copy-name' : 'pdapp-env-copy-value'}
      >
        {done ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      </button>
    )
  }

  const addButton = canEdit && !draft && (
    <button
      type="button"
      onClick={() => { setError(null); setDraft(emptyDraft()) }}
      disabled={atMax}
      className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm themed-btn-secondary px-3 py-1.5 rounded disabled:opacity-40"
      data-testid="pdapp-env-add"
      title={atMax ? t('appEnv.atMax', { count: maxVars }) : undefined}
    >
      <Plus size={14} /> {t('appEnv.add')}
    </button>
  )

  /** A key, as a chip: its connector icon (the Keybook page's) and its name. */
  const keyChip = (label: string, credentialType: string | null | undefined, title?: string) =>
    <KeyChip label={label} credentialType={credentialType} title={title} />

  const valueCell = (v: AppEnvVar) => {
    if (v.kind === 'plain') {
      if (v.unset) return <em className="block text-amber-400">{t('appEnv.unsetValue')}</em>
      if (!v.value) return <em className="block themed-text-muted">{t('appEnv.emptyValue')}</em>
      return (
        <span className="flex items-center gap-1 min-w-0">
          <span className="font-mono themed-text-secondary truncate" title={v.value}>{v.value}</span>
          {copyButton(v, 'value', v.value)}
        </span>
      )
    }
    const s = v.secret
    if (s?.status === 'ok') {
      // The value never reaches this screen: the ONE thing to know is which key it comes from (owner: the pill alone,
      // no dots). The field only when it isn't the usual one.
      const field = s.field && s.field !== DEFAULT_FIELD ? s.field : null
      return (
        <>
          <span className="flex items-center gap-2 min-w-0">
            {keyChip(s.label ?? t('appEnv.aKey'), s.credentialType, `${s.label ?? ''} · ${s.field ?? ''}`)}
            {field && <span className="themed-text-muted font-mono shrink-0">{field}</span>}
          </span>
          {s.description && <span className="block mt-0.5 themed-text-muted truncate" title={s.description}>{s.description}</span>}
        </>
      )
    }
    return (
      <>
        {s?.status === 'no-access' ? (
          <span className="inline-flex items-center gap-1 themed-chip-warn" data-testid="pdapp-env-no-access">
            <Lock size={11} className="shrink-0" />{t('appEnv.statusNoAccess')}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <span className="inline-flex items-start gap-1 themed-chip-warn min-w-0" data-testid="pdapp-env-needs-key" title={s?.label ? `${s.label} · ${s.field}` : undefined}>
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span className="min-w-0">
                {s?.needs === 'unavailable' ? t('appEnv.needsUnavailable') : s?.needs === 'no-key' ? t('appEnv.needsNoKey') : s?.needs === 'no-field' ? t('appEnv.needsNoField') : t('appEnv.needsConnect')}
              </span>
            </span>
            {s?.label && keyChip(s.label, s.credentialType)}
            {s?.canConnect && (
              <button type="button" onClick={() => connect(v)} disabled={busy} className="text-xs px-2 py-0.5 rounded themed-btn-primary disabled:opacity-40" data-testid="pdapp-env-connect">
                {t('appEnv.connect')}
              </button>
            )}
          </span>
        )}
        {s?.description && <span className="block mt-0.5 themed-text-muted truncate" title={s.description}>{s.description}</span>}
      </>
    )
  }

  const row = (v: AppEnvVar) => (
    <li
      key={v.name}
      className={`group flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2.5 border-b themed-border last:border-b-0 themed-bg-hover ${ROW_GRID}`}
      data-testid="pdapp-env-row" data-name={v.name} data-kind={v.kind}
    >
      {/* Stacked (narrow): the name takes what the type and the actions leave on the first line and truncates there
          (basis 0, so a long name never pushes the actions onto a line of their own), growing no wider than itself so a
          short name keeps its type beside it; the value gets the next line. */}
      <div className="order-1 grow basis-0 max-w-fit @xl/envlist:max-w-none flex items-center gap-1 min-w-0">
        <code className="text-xs font-mono font-medium themed-text truncate" title={v.name}>{v.name}</code>
        {copyButton(v, 'name', v.name)}
      </div>
      <div className="order-2">
        {v.kind === 'secret' ? (
          <SecretBadge label={t('appEnv.kindSecret')} />
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-muted">
            <TypeIcon size={10} />{t('appEnv.kindPlain')}
          </span>
        )}
      </div>
      <div className="order-4 basis-full @xl/envlist:basis-auto @xl/envlist:order-3 min-w-0 text-xs">{valueCell(v)}</div>
      <div className="order-3 @xl/envlist:order-4 ml-auto @xl/envlist:ml-0 flex items-center justify-end gap-0.5 shrink-0">
        {canEdit && (
          <button type="button" onClick={() => startEdit(v)} disabled={busy} data-env-edit={v.name} className={`${ACTION_BTN} hover:themed-text`} aria-label={t('appEnv.edit', { name: v.name })} title={t('appEnv.edit', { name: v.name })}>
            <Pencil size={14} />
          </button>
        )}
        {(canEdit || (v.kind === 'secret' && (v.secret?.status === 'ok' || v.secret?.status === 'no-access'))) && (
          <button type="button" onClick={() => remove(v)} disabled={busy} className={`${ACTION_BTN} hover:text-red-400`} aria-label={t('appEnv.removeLabel', { name: v.name })} title={t('appEnv.removeLabel', { name: v.name })}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </li>
  )

  const nameField = draft && (
    <div className="min-w-0">
      <label className="block text-xs themed-text-muted mb-1" htmlFor="pdapp-env-name">{t('appEnv.name')}</label>
      <input
        id="pdapp-env-name"
        value={draft.name}
        onChange={e => setDraft({ ...draft, name: e.target.value.trim() })}
        placeholder="API_TOKEN"
        autoFocus
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className="themed-input text-sm font-mono px-2 py-1.5 rounded w-full"
        data-testid="pdapp-env-name"
      />
      {draft.name && nameProblem && (
        <p className="text-xs text-red-400 mt-1">{nameProblem === 'reserved' ? t('appEnv.nameReserved') : t('appEnv.nameFormat')}</p>
      )}
      {duplicate && <p className="text-xs text-red-400 mt-1">{t('appEnv.nameTaken')}</p>}
    </div>
  )

  // The picked key's name when it isn't in this person's key list (yet, or at all): the label the server sent for the
  // variable being edited — the server sends it only when the viewer can read the key.
  const editedVar = draft?.original ? vars.find(v => v.name === draft.original) : undefined
  const pickerFallback = draft?.entryId && editedVar?.secret?.entryId === draft.entryId && editedVar.secret.label
    ? { label: editedVar.secret.label, credentialType: editedVar.secret.credentialType }
    : undefined

  const form = draft && (
    <div className="p-3 sm:p-4 space-y-3 themed-bg-secondary" data-testid="pdapp-env-form">
      <Choice
        label={t('appEnv.type')}
        value={draft.kind}
        options={[
          { key: 'plain', label: t('appEnv.kindPlainLong'), icon: <TypeIcon size={11} />, testId: 'pdapp-env-kind-plain' },
          { key: 'secret', label: t('appEnv.kindSecretLong'), icon: <Lock size={11} />, disabled: !keybookEnabled, testId: 'pdapp-env-kind-secret' },
        ]}
        // A value typed for one kind is never carried into the other: a secret typed into the masked field
        // must not appear in clear (or be saved) as Text.
        onChange={k => k !== draft.kind && setDraft({ ...draft, kind: k, value: '' })}
      />

      {draft.kind === 'plain' ? (
        <div className="grid gap-3 @xl/envlist:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          {nameField}
          <div className="min-w-0">
            <label className="block text-xs themed-text-muted mb-1" htmlFor="pdapp-env-value">{t('appEnv.value')}</label>
            <textarea
              id="pdapp-env-value"
              value={draft.value}
              onChange={e => setDraft({ ...draft, value: e.target.value })}
              rows={1}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              className="themed-input text-sm font-mono px-2 py-1.5 rounded w-full min-h-[2.125rem] resize-y block"
              data-testid="pdapp-env-value"
            />
            <p className="text-[11px] themed-text-muted mt-1">{t('appEnv.plainHint')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {nameField}
          <Choice
            variant="cards"
            label={t('appEnv.secretSource')}
            value={draft.source}
            options={[
              { key: 'new', label: t('appEnv.sourceNew'), hint: t('appEnv.sourceNewHint'), icon: <ClipboardPaste size={15} />, testId: 'pdapp-env-source-new' },
              { key: 'keybook', label: t('appEnv.sourceKeybook'), hint: t('appEnv.sourceKeybookHint'), icon: <BookKey size={15} />, testId: 'pdapp-env-source-keybook' },
            ]}
            // A secret typed for "Paste a new secret" isn't kept while a key is picked instead.
            onChange={src => setDraft({ ...draft, source: src, value: '' })}
          />
          {draft.source === 'new' ? (
            <div>
              <label className="block text-xs themed-text-muted mb-1" htmlFor="pdapp-env-secret">{t('appEnv.secretValue')}</label>
              <div className="relative">
                <input
                  id="pdapp-env-secret"
                  type={reveal ? 'text' : 'password'}
                  // An app's secret belongs in the Keybook, not the browser's password list: ask the browser and the
                  // common password managers not to offer to save or fill it.
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  data-bwignore=""
                  // Revealed, this is a text field: a phone keyboard would capitalise, autocorrect and learn
                  // what is typed into it — a password field never does. Keep the secret as typed.
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  value={draft.value}
                  onChange={e => setDraft({ ...draft, value: e.target.value })}
                  className="themed-input text-sm font-mono pl-2 pr-9 py-1.5 rounded w-full"
                  data-testid="pdapp-env-secret-value"
                />
                <button
                  type="button"
                  onClick={() => setReveal(r => !r)}
                  aria-label={reveal ? t('appEnv.hideValue') : t('appEnv.showValue')}
                  title={reveal ? t('appEnv.hideValue') : t('appEnv.showValue')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded themed-text-muted hover:themed-text"
                  data-testid="pdapp-env-secret-reveal"
                >
                  {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
              <p className="text-[11px] themed-text-muted mt-1">{t('appEnv.newSecretHint', { label: `${appName} · ${draft.name || '…'}` })}</p>
            </div>
          ) : (
            <KeyFieldPicker
              value={{ entryId: draft.entryId, field: draft.field }}
              onChange={v => setDraft(d => d ? { ...d, entryId: v.entryId, field: v.field } : d)}
              fallback={pickerFallback}
            />
          )}
          <div>
            <label className="block text-xs themed-text-muted mb-1" htmlFor="pdapp-env-description">{t('appEnv.descriptionLabel')}</label>
            <input
              id="pdapp-env-description"
              value={draft.description}
              maxLength={300}
              onChange={e => setDraft({ ...draft, description: e.target.value })}
              placeholder={t('appEnv.descriptionPlaceholder')}
              className="themed-input text-sm px-2 py-1.5 rounded w-full"
              data-testid="pdapp-env-description"
            />
          </div>
          <p className="flex items-start gap-1.5 text-[11px] themed-text-muted"><Info size={12} className="shrink-0 mt-px" />{t('appEnv.secretHint')}</p>
        </div>
      )}

      {error && <p className="text-xs text-red-400" role="alert">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={() => { focusAfterForm.current = orphaned ? '' : draft.original ?? ''; setDraft(null); setError(null) }} disabled={busy} className="text-sm px-3 py-1.5 rounded themed-text-muted hover:themed-text">{t('appEnv.cancel')}</button>
        <button type="button" onClick={() => { setReveal(false); focusAfterForm.current = draft.name; void save() }} disabled={!canSave} className="text-sm px-3 py-1.5 rounded themed-btn-primary disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="pdapp-env-save">
          {busy && <Loader2 size={13} className="animate-spin" />}{t('appEnv.save')}
        </button>
      </div>
    </div>
  )

  const adding = !!draft && draft.original === null
  // An edit whose row is gone (deleted or renamed elsewhere, then re-listed) still has its form: shown at the top, so
  // the draft never hides with Add and its errors.
  const orphaned = !!draft && draft.original !== null && !vars.some(v => v.name === draft.original)
  // Likewise an edit whose row a re-list moved onto another page: its form stays at the top of the page in view.
  const editOffPage = !!draft && draft.original !== null && !pageVars.some(v => v.name === draft.original)
  const showPager = shownVars.length > PAGE_SIZE
  // What the live region says after a copy; an invisible mark alternates so the same words are announced again.
  const copiedText = copied
    ? (copied.what === 'name' ? t('appEnv.copiedName', { name: copied.name }) : t('appEnv.copiedValue', { name: copied.name })) + (copied.n % 2 ? '​' : '')
    : ''

  return (
    <section data-testid="pdapp-env" ref={sectionRef}>
      <span className="sr-only" aria-live="polite" data-testid="pdapp-env-live">{copiedText}</span>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-1">
        <KeyRound size={16} className="themed-icon" />
        <h2 className="text-sm font-semibold themed-text">{t('appEnv.title')}</h2>
        <div className="flex-1" />
        {vars.length > 0 && addButton}
      </div>
      <p className="text-xs themed-text-muted mb-3">{t('appEnv.description')}</p>

      {!installed && (
        <Notice icon={<AlertTriangle size={14} />} testId="pdapp-env-not-installed">{t('appEnv.notInstalled')}</Notice>
      )}
      {!keybookEnabled && (
        <Notice icon={<AlertTriangle size={14} />} testId="pdapp-env-keybook-off">{t('appEnv.keybookOff')}</Notice>
      )}
      {problems.length > 0 && (
        <Notice icon={<AlertTriangle size={14} />} testId="pdapp-env-problems">
          <p className="font-medium mb-1">{t('appEnv.fileProblems')}</p>
          <ul className="list-disc pl-4 space-y-0.5">{problems.map(p => <li key={p}>{p}</li>)}</ul>
        </Notice>
      )}
      {blockedKeys > 0 && (
        <Notice icon={<Lock size={14} />} testId="pdapp-env-blocked">{t('appEnv.blockedByKeys', { count: blockedKeys })}</Notice>
      )}

      {vars.length === 0 && !draft ? (
        <div className="flex flex-col items-center text-center gap-2 px-4 py-6 rounded-lg border border-dashed themed-border" data-testid="pdapp-env-empty">
          <Variable size={20} className="themed-icon" />
          {/* "Add one…" only to someone who can: a manager who may only take keys away sees no Add. */}
          <p className="text-xs themed-text-muted">{canEdit ? t('appEnv.empty') : t('appEnv.emptyReadOnly')}</p>
          {addButton}
        </div>
      ) : (
        <div className="@container/envlist rounded-lg border themed-border overflow-hidden" data-testid="pdapp-env-table">
          {searchable && (
            <div className="relative border-b themed-border">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted" />
              <input
                type="search"
                value={query}
                onChange={e => { setQuery(e.target.value); setPage(0) }}
                placeholder={t('appEnv.searchVars')}
                aria-label={t('appEnv.searchVars')}
                className="text-sm pl-8 pr-3 py-2 w-full bg-transparent themed-text outline-none"
                data-testid="pdapp-env-search"
              />
            </div>
          )}
          <ul className={LIST_GRID}>
            {vars.length > 0 && (
              <li className={`hidden ${ROW_GRID} px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide themed-text-muted themed-bg-secondary border-b themed-border`} aria-hidden data-testid="pdapp-env-columns">
                <span>{t('appEnv.name')}</span>
                <span>{t('appEnv.type')}</span>
                <span>{t('appEnv.value')}</span>
                <span />
              </li>
            )}
            {(adding || editOffPage) && <li className={`border-b themed-border last:border-b-0 ${FULL_ROW}`}>{form}</li>}
            {pageVars.map(v => draft?.original === v.name
              ? <li key={v.name} className={`border-b themed-border last:border-b-0 ${FULL_ROW}`}>{form}</li>
              : row(v))}
            {q && shownVars.length === 0 && (
              <li className={`px-3 py-3 text-xs themed-text-muted text-center ${FULL_ROW}`} data-testid="pdapp-env-no-match">{t('appEnv.noVarMatch')}</li>
            )}
          </ul>
          {showPager && (
            <nav className="flex items-center justify-between gap-2 px-3 py-2 border-t themed-border text-xs themed-text-muted" aria-label={t('appEnv.pages')} data-testid="pdapp-env-pager">
              <span className="tabular-nums" data-testid="pdapp-env-page-range">
                {t('appEnv.pageRange', { from: curPage * PAGE_SIZE + 1, to: curPage * PAGE_SIZE + pageVars.length, total: shownVars.length })}
              </span>
              <span className="flex items-center gap-1">
                {/* aria-disabled, not disabled: reaching the first / last page must not throw the focused button's
                    focus to the page. */}
                <button type="button" onClick={() => { if (curPage > 0) setPage(curPage - 1) }} aria-disabled={curPage === 0} className={`${ACTION_BTN} hover:themed-text aria-disabled:opacity-40 aria-disabled:cursor-default`} aria-label={t('appEnv.prevPage')} title={t('appEnv.prevPage')} data-testid="pdapp-env-prev-page">
                  <ChevronLeft size={14} />
                </button>
                <span className="tabular-nums px-1" aria-live="polite">{t('appEnv.pageOf', { page: curPage + 1, pages: pageCount })}</span>
                <button type="button" onClick={() => { if (curPage < pageCount - 1) setPage(curPage + 1) }} aria-disabled={curPage >= pageCount - 1} className={`${ACTION_BTN} hover:themed-text aria-disabled:opacity-40 aria-disabled:cursor-default`} aria-label={t('appEnv.nextPage')} title={t('appEnv.nextPage')} data-testid="pdapp-env-next-page">
                  <ChevronRight size={14} />
                </button>
              </span>
            </nav>
          )}
        </div>
      )}
      <p className="flex items-start gap-1.5 text-[11px] themed-text-muted mt-2"><Info size={12} className="shrink-0 mt-px" />{t('appEnv.editNeedsKeys')}</p>

      {unusedKeys.length > 0 && (
        <div className="mt-4" data-testid="pdapp-env-unused-keys">
          <p className="text-xs themed-text-muted mb-1.5">{t('appEnv.unusedKeys')}</p>
          <ul className="rounded-lg border themed-border overflow-hidden">
            {unusedKeys.map(k => (
              <li key={k.entryId} className="flex items-center gap-2 px-3 py-2 border-t themed-border first:border-t-0 text-xs">
                <KeyRound size={12} className="themed-icon shrink-0" />
                <span className="themed-text truncate flex-1">{k.label ?? t('appEnv.aKeyYouCantOpen')}</span>
                <button type="button" onClick={() => takeUnusedKey(k)} disabled={busy} className={`${ACTION_BTN} hover:text-red-400`} aria-label={t('appEnv.takeKey')} title={t('appEnv.takeKey')}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!draft && error && <p className="text-xs text-red-400 mt-2" role="alert">{error}</p>}
    </section>
  )
}
